import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation, LanguageSwitcher } from '../i18n';

export default function Login() {
  const { login } = useAuth();
  const { orgName } = useOrg();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.error || t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcher />
      </div>
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <img src="/logo.svg" alt={orgName} style={{ height: 64 }} />
          <h1 style={styles.title}>{orgName}</h1>
          <p style={styles.subtitle}>{t('login.managementSystem')}</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}
          
          <div style={styles.field}>
            <label style={styles.label}>{t('login.username')}</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              style={styles.input}
              placeholder={t('login.enterUsername')}
              autoComplete="username"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{t('login.password')}</label>
            <div style={styles.passwordWrap}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={styles.input}
                placeholder={t('login.enterPassword')}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeBtn}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} style={styles.btn}>
            {loading ? t('login.loggingIn') : t('login.login')}
          </button>
        </form>

        <p style={styles.footer}>{t('login.version')}</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: 'linear-gradient(135deg, #0c4a6e 0%, #0ea5e9 100%)',
    padding: 20,
  },
  card: {
    background: '#fff', borderRadius: 16, padding: '40px 32px',
    width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  logoSection: { textAlign: 'center', marginBottom: 32 },
  title: { fontSize: 28, fontWeight: 800, color: '#0c4a6e', margin: '12px 0 4px' },
  subtitle: { fontSize: 14, color: '#64748b' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: '#334155' },
  input: {
    padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db',
    fontSize: 14, width: '100%', boxSizing: 'border-box',
    outline: 'none', transition: 'border 0.2s',
  },
  passwordWrap: { position: 'relative' },
  eyeBtn: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8',
  },
  btn: {
    padding: '12px', borderRadius: 8, border: 'none',
    background: '#0c4a6e', color: '#fff', fontSize: 15,
    fontWeight: 600, cursor: 'pointer', marginTop: 8,
  },
  error: {
    background: '#fef2f2', color: '#dc2626', padding: '10px 14px',
    borderRadius: 8, fontSize: 13, fontWeight: 500,
  },
  footer: { textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 24 },
};
